import PropTypes from "prop-types";
import { useMemo } from "react";

import { Pencil, Trash } from "lucide-react";
import { Badge, DefaultTable, ButtonIcon, cn } from "twyne-new.ui";

import { TruncateWithTooltipFormatter } from "@/components/Grid/components/Table/formatters";
import { DefaultDragFormatter, DndSortableList, useSortableDndListItem } from "@/components/SortableDnd";

import { DroppableArea } from "./Draggable";
import Droppable from "./Droppable";
import { PageDetails, TYPE_ALAIASES } from "./utils";

function SortableTableRow({ children, row, className, onMove }) {
  const dnd = useSortableDndListItem(row.id)

  return <>
    <DroppableArea origin="active" onMove={onMove} node={{ index: row.index }} type="page-item" />
    <tr
      ref={dnd.setNodeRef}
      style={dnd.style}
      className={cn(className, !row.active && "twy-bg-base ")}
    >
      {children}
    </tr>
  </>
}

export default function SortablePagesLegacy({ items, onClick, onMove, onRemove, onDropLast, onReorder }) {
  const columns = useMemo(() => [
    {
      dataField: '',
      formatter: (_, row) => <DefaultDragFormatter row={row} />,
      text: "",
    }, {
      dataField: 'name',
      formatter: nameFormatter,
      text: "Name",
      headerClassName: 'text-gray-800! dark:text-gray-700!',
      className: 'twy-text-base-accent',
    }, {
      dataField: 'type',
      formatter: typeFormatter,
      text: "Type",
      headerClassName: 'text-gray-800! dark:text-gray-700!',
    }, {
      dataField: 'active',
      formatter: activeFormatter,
      text: "Active",
      headerClassName: 'text-gray-800! dark:text-gray-700!',
    }, {
      dataField: 'rules',
      formatter: rulesFormatter,
      text: "Rules",
      headerClassName: 'text-gray-800! dark:text-gray-700!',
    }, {
      dataField: 'dynamicNavigation',
      formatter: dynamicFormatter,
      text: "Dynamic",
      headerClassName: 'text-gray-800! dark:text-gray-700!',
    }, {
      dataField: '',
      formatter: (_, row) => actionFormatter(_, row, onRemove, onClick),
      text: '',
      headerClassName: 'text-gray-800! dark:text-gray-700!',
      className: 'w-8',
    },
  ], [onClick, onRemove])

  if (!items?.length) {
    return <div className="ml-4">
      <Droppable tag="div" type="page-item" className="p-6 w-full flex items-center justify-center twy-subtle-neutral border rounded twy-text-base-accent border-dashed" onMove={onMove} node={{ index: 0 }}>
        Drag page here to add it to this path
      </Droppable>
    </div>
  }

  return <DndSortableList items={items} onSortEnd={onReorder}>
    <DefaultTable
      columns={columns}
      data={items}
      className="pl-4 h-[calc(100vh-32.5rem)] overflow-y-scroll overflow-x-hidden"
      rowWrapper={props => <SortableTableRow {...props} onMove={onMove} />}
      keyField="id"
      footer={<Droppable onMove={onDropLast} node={{}} />}
    />
  </DndSortableList>
}

const nameFormatter = (name, row) => <TruncateWithTooltipFormatter value={`#${row.id} - ${name}`} />

const typeFormatter = (_, row) => <>
  {TYPE_ALAIASES[row.type].label}<br />
  <PageDetails data={row} />
</>

const activeFormatter = active => <Badge color={`${active ? 'accent' : 'neutral'}`} variant='outline'>
  {active ? 'yes' : 'no'}
</Badge>

const rulesFormatter = (_, { path_pages, rules }) => <Badge color={`${rules || path_pages?.rules ? 'accent' : 'neutral'}`} variant='outline'>
  {rules || path_pages?.rules ? 'yes' : 'no'}
</Badge>

const dynamicFormatter = (_, { dynamicNavigation }) => <Badge color={`${dynamicNavigation ? 'accent' : 'neutral'}`} variant='outline'>
  {dynamicNavigation ? 'yes' : 'no'}
</Badge>

const actionFormatter = (_, row, onRemove, onClick) => {
  const handleRemove = e => {
    e.stopPropagation()
    onRemove(row)
  }

  return <div className='flex items-center gap-1'>
    <ButtonIcon content='Edit page'>
      <Pencil size={16} className="cursor-pointer hover:twy-text-base-accent" onClick={() => onClick(row)} />
    </ButtonIcon>
    <ButtonIcon content='Delete page'>
      <Trash size={16} onClick={handleRemove} />
    </ButtonIcon>
  </div>
}

SortablePagesLegacy.propTypes = {
  items: PropTypes.array,
  onDropLast: PropTypes.func,
  onClick: PropTypes.func,
  onMove: PropTypes.func,
  onRemove: PropTypes.func,
};
SortablePagesLegacy.defaultProps = {};
